import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EASE } from "./Reveal";
import { scrollToId } from "@/hooks/useLenis";

const LINKS = [
  { label: "The Coffee", id: "coffee" },
  { label: "Journal", id: "journal" },
  { label: "About", id: "about" },
];

const go = (id) => scrollToId(id);

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = (id) => {
    setOpen(false);
    setTimeout(() => go(id), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-paper/90 backdrop-blur-md border-b border-linen" : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="flex items-center justify-between px-6 md:px-10 py-3 md:py-4">
          <button
            onClick={() => window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo({ top: 0, behavior: "smooth" })}
            className="group pr-4 -ml-1"
            data-testid="nav-brand-link"
            aria-label="Anichya Coffee — back to top"
          >
            <span className="block leading-none text-center" data-testid="nav-logo">
              <span className="block font-serif text-[13px] md:text-[15px] tracking-[0.18em] text-brown">
                ANICHYA
              </span>
              <span className="block font-serif text-[8px] md:text-[9px] tracking-[0.5em] -mr-[0.5em] text-brown mt-1">
                COFFEE
              </span>
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => nav(l.id)}
                className="link-underline text-[11px] uppercase tracking-[0.2em] text-ink/70 hover:text-ink transition-colors"
                data-testid={`nav-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("contact")}
              className="link-underline text-[11px] uppercase tracking-[0.2em] text-ink/70 hover:text-ink transition-colors"
              data-testid="nav-contact-link"
            >
              Get in Touch
            </button>
            <button
              onClick={() => nav("join")}
              className="rounded-full bg-forest text-paper text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 hover:bg-brown transition-colors duration-300"
              data-testid="nav-join-button"
            >
              Join the List
            </button>
          </nav>

          <button
            className="md:hidden text-ink p-1"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-40 bg-paper md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex h-full flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE, delay: 0.08 + i * 0.06 }}
                  onClick={() => nav(l.id)}
                  className="text-left font-serif text-4xl text-ink py-2"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE, delay: 0.28 }}
                onClick={() => nav("contact")}
                className="link-underline text-left text-sm uppercase tracking-[0.2em] text-ink/80 w-max mt-6"
                data-testid="nav-mobile-contact-link"
              >
                Get in Touch
              </motion.button>
              <motion.button
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE, delay: 0.34 }}
                onClick={() => nav("join")}
                className="mt-6 w-max rounded-full bg-forest text-paper text-xs uppercase tracking-[0.2em] px-7 py-3.5"
                data-testid="nav-mobile-join-button"
              >
                Join the List
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
